import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getProducts, getSliders, getProjects, getTestimonials, getSettings, getBanners,
    adminGetInquiries, adminDeleteInquiry,
    adminCreateProduct, adminUpdateProduct, adminDeleteProduct,
    adminCreateSlider, adminUpdateSlider, adminDeleteSlider,
    adminCreateProject, adminUpdateProject, adminDeleteProject,
    adminCreateTestimonial, adminUpdateTestimonial, adminApproveTestimonial, adminDeleteTestimonial,
    adminUpdateSettings, adminUpdateBanner, adminUpdateCredentials, resolveImageUrl
} from './services/api';

// MODULAR SUB-COMPONENTS
import SidebarNav from './components/SidebarNav';
import DashboardOverviewTab from './components/tabs/DashboardOverviewTab';
import ProductsTab from './components/tabs/ProductsTab';
import SlidersTab from './components/tabs/SlidersTab';
import ProjectsTab from './components/tabs/ProjectsTab';
import TestimonialsTab from './components/tabs/TestimonialsTab';
import PageBannersTab from './components/tabs/PageBannersTab';
import InquiriesTab from './components/tabs/InquiriesTab';
import SettingsTab from './components/tabs/SettingsTab';

// MODAL SUB-COMPONENTS
import ProductModal from './components/modals/ProductModal';
import SliderModal from './components/modals/SliderModal';
import ProjectModal from './components/modals/ProjectModal';
import TestimonialModal from './components/modals/TestimonialModal';
import PageBannerModal from './components/modals/PageBannerModal';
import ConfirmDeleteModal from './components/modals/ConfirmDeleteModal';

import './Admin.css';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [token, setToken] = useState(localStorage.getItem('adminToken') || '');
    const [user] = useState(JSON.parse(localStorage.getItem('adminUser') || '{}'));

    const [activeTab, setActiveTab] = useState('dashboard');
    const [statusMsg, setStatusMsg] = useState({ type: '', text: '' });

    // Data Collections
    const [products, setProducts] = useState([]);
    const [sliders, setSliders] = useState([]);
    const [projects, setProjects] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [inquiries, setInquiries] = useState([]);
    const [banners, setBanners] = useState({});
    const [settings, setSettings] = useState({});
    const [achievements, setAchievements] = useState({
        yearsExperience: '16+',
        projectsCompleted: '1100+',
        clientSatisfaction: '100%'
    });

    // Modals & Active Edit Objects
    const [modalType, setModalType] = useState(null);
    const [editItem, setEditItem] = useState(null);
    const [modalImageUrl, setModalImageUrl] = useState('');
    const [modalImageUrls, setModalImageUrls] = useState([]);
    const [modalBannerImageUrl, setModalBannerImageUrl] = useState('');
    const [modalContent, setModalContent] = useState([]);
    const [modalSpecs, setModalSpecs] = useState([]);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const openModal = (type, item = null) => {
        setEditItem(item);
        setModalType(type);
        if (item) {
            const bRecord = (item.slug && banners[item.slug]) || {};
            const mainImgVal = item.mainImage || item.image || '';
            const bannerRecImg = bRecord.bgImage && bRecord.bgImage !== mainImgVal ? bRecord.bgImage : '';
            setModalImageUrl(mainImgVal || item.bgImage || '');
            setModalImageUrls([mainImgVal || item.bgImage || '']);
            setModalBannerImageUrl(item.bannerImage || bannerRecImg);
            setModalContent(item.content || []);
            let specs = item.specifications;
            if (typeof specs === 'string') {
                try { specs = JSON.parse(specs); } catch (e) { specs = []; }
            }
            if (Array.isArray(specs) && specs.length > 0) {
                setModalSpecs(specs.map(s => ({ key: s.key || '', value: s.value || '' })));
            } else {
                setModalSpecs([]);
            }
        } else {
            setModalImageUrl('');
            setModalImageUrls([]);
            setModalBannerImageUrl('');
            setModalContent([]);
            setModalSpecs([]);
        }
    };

    const addSpecRow = () => {
        setModalSpecs(prev => [...prev, { key: '', value: '' }]);
    };

    const updateSpecRow = (index, field, value) => {
        setModalSpecs(prev => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    const removeSpecRow = (index) => {
        setModalSpecs(prev => prev.filter((_, i) => i !== index));
    };

    // Check Auth
    useEffect(() => {
        const savedToken = localStorage.getItem('adminToken');
        if (!savedToken) {
            navigate('/login');
        } else {
            setToken(savedToken);
            loadAllAdminData(savedToken);
        }
    }, [navigate]);

    const showMsg = (type, text) => {
        setStatusMsg({ type, text });
        setTimeout(() => setStatusMsg({ type: '', text: '' }), 5000);
    };

    const getImageUrl = (url, fallback = '/images/products/acp_cladding.jpg') => {
        if (!url) return fallback;
        if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
            return url;
        }
        return resolveImageUrl(url);
    };

    const loadAllAdminData = async (tok) => {
        try {
            const pList = await getProducts(); setProducts(pList || []);
            const sList = await getSliders(); setSliders(sList || []);
            const prList = await getProjects(); setProjects(prList || []);
            const tList = await getTestimonials(); setTestimonials(tList || []);
            const bMap = await getBanners(); setBanners(bMap || {});
            const inqRes = await adminGetInquiries(tok);
            if (inqRes && inqRes.success) setInquiries(inqRes.data || []);
            const stRes = await getSettings();
            if (stRes) {
                setSettings(stRes.settings || {});
                if (stRes.achievements) setAchievements(stRes.achievements);
            }
        } catch (e) {
            console.warn('[AdminDashboard] Fetch warning:', e);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/login');
    };

    // PRODUCT ACTIONS
    const handleSaveProduct = async (e) => {
        e.preventDefault();
        const form = e.target;

        const rawTitle = form.title.value || 'New Product';
        const userSlug = form.slug?.value ? form.slug.value.trim() : '';
        const cleanSlug = (userSlug || rawTitle).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || `prod-${Date.now()}`;

        const cleanSpecs = modalSpecs.filter(s => s.key && s.key.trim() && s.value && s.value.trim());

        const prodData = {
            title: rawTitle,
            slug: cleanSlug,
            category: form.category.value,
            shortDesc: form.shortDesc.value,
            fullDesc: form.fullDesc.value,
            mainImage: modalImageUrl || form.mainImage?.value || '/images/products/acp_cladding.jpg',
            bannerImage: modalBannerImageUrl || '',
            features: form.features.value ? form.features.value.split('\n').filter(Boolean) : [],
            specifications: cleanSpecs
        };

        if (editItem && editItem.id) {
            const res = await adminUpdateProduct(editItem.id, prodData, token);
            if (res.success) {
                showMsg('success', 'Changes Saved Successfully!');
                loadAllAdminData(token);
                setModalType(null);
            } else showMsg('danger', res.message || 'Error updating product');
        } else {
            const res = await adminCreateProduct(prodData, token);
            if (res.success) {
                showMsg('success', 'Changes Saved Successfully!');
                loadAllAdminData(token);
                setModalType(null);
            } else showMsg('danger', res.message || 'Error creating product');
        }
    };

    const handleDeleteProduct = (id) => {
        const prod = products.find(p => p.id === id);
        const name = prod?.title ? `"${prod.title}"` : 'this product';
        setDeleteConfirm({
            title: 'Delete Product',
            message: `Are you sure you want to delete ${name}? This action cannot be undone.`,
            onConfirm: async () => {
                setDeleteConfirm(null);
                const res = await adminDeleteProduct(id, token);
                if (res.success) {
                    showMsg('success', 'Product deleted.');
                    loadAllAdminData(token);
                } else showMsg('danger', res.message);
            }
        });
    };

    // SLIDER ACTIONS
    const handleSaveSlider = async (e) => {
        e.preventDefault();
        const form = e.target;

        const badgeValue = form.badge.value.trim();
        const sliderData = {
            badge: badgeValue,
            tag: badgeValue,
            title: form.title.value,
            description: form.description.value,
            btn1Text: form.btn1Text.value,
            btn1Link: form.btn1Link.value,
            image: modalImageUrl || editItem?.image || '/images/banner/6.jpg',
            order: parseInt(form.order.value) || 1
        };

        if (editItem && editItem.id) {
            const res = await adminUpdateSlider(editItem.id, sliderData, token);
            if (res.success) {
                showMsg('success', 'Changes Saved Successfully!');
                loadAllAdminData(token);
                setModalType(null);
            } else showMsg('danger', res.message);
        } else {
            const res = await adminCreateSlider(sliderData, token);
            if (res.success) {
                showMsg('success', 'Changes Saved Successfully!');
                loadAllAdminData(token);
                setModalType(null);
            } else showMsg('danger', res.message);
        }
    };

    const handleDeleteSlider = (id) => {
        const slide = sliders.find(s => s.id === id);
        const name = slide?.title ? `"${slide.title}"` : 'this slide';
        setDeleteConfirm({
            title: 'Delete Hero Slide',
            message: `Are you sure you want to delete ${name}? This action cannot be undone.`,
            onConfirm: async () => {
                setDeleteConfirm(null);
                const res = await adminDeleteSlider(id, token);
                if (res.success) {
                    showMsg('success', 'Slide removed.');
                    loadAllAdminData(token);
                } else showMsg('danger', res.message);
            }
        });
    };

    // PROJECT ACTIONS
    const handleSaveProject = async (e, bulkCategory, bulkImages) => {
        if (e && e.preventDefault) e.preventDefault();

        if (editItem && editItem.id) {
            const form = e?.target;
            const selectedCat = (form && form.category?.value) ? form.category.value : (bulkCategory || 'ACP Front Elevation');
            const projData = {
                title: selectedCat,
                category: selectedCat,
                mainImage: modalImageUrl || editItem?.mainImage || '/images/products/acp_cladding.jpg'
            };
            const res = await adminUpdateProject(editItem.id, projData, token);
            if (res.success) {
                showMsg('success', 'Project Image Updated Successfully!');
                loadAllAdminData(token);
                setModalType(null);
            } else showMsg('danger', res.message);
        } else {
            // Bulk Image Upload Creation
            const selectedCat = bulkCategory || 'ACP Front Elevation';
            const imagesToSave = (bulkImages && bulkImages.length > 0) ? bulkImages : (modalImageUrl ? [modalImageUrl] : []);

            if (imagesToSave.length === 0) {
                showMsg('danger', 'Please select at least one project image.');
                return;
            }

            let successCount = 0;
            for (const imgUrl of imagesToSave) {
                const projData = {
                    title: selectedCat,
                    category: selectedCat,
                    mainImage: imgUrl
                };
                const res = await adminCreateProject(projData, token);
                if (res && res.success) successCount++;
            }

            if (successCount > 0) {
                showMsg('success', `Successfully Uploaded & Saved ${successCount} Project Image(s)!`);
                loadAllAdminData(token);
                setModalType(null);
            } else {
                showMsg('danger', 'Failed to save project images.');
            }
        }
    };

    const handleDeleteProject = (id) => {
        const proj = projects.find(p => p.id === id);
        const name = proj?.title ? `"${proj.title}"` : 'this project';
        setDeleteConfirm({
            title: 'Delete Project',
            message: `Are you sure you want to delete ${name}? This action cannot be undone.`,
            onConfirm: async () => {
                setDeleteConfirm(null);
                const res = await adminDeleteProject(id, token);
                if (res.success) {
                    showMsg('success', 'Project deleted.');
                    loadAllAdminData(token);
                } else showMsg('danger', res.message);
            }
        });
    };

    // TESTIMONIAL ACTIONS
    const handleSaveTestimonial = async (e) => {
        e.preventDefault();
        const form = e.target;

        const tData = {
            name: form.name.value,
            rating: parseInt(form.rating.value) || 5,
            quote: form.quote.value
        };

        if (editItem && editItem.id) {
            const res = await adminUpdateTestimonial(editItem.id, tData, token);
            if (res.success) {
                showMsg('success', 'Changes Saved Successfully!');
                loadAllAdminData(token);
                setModalType(null);
            } else showMsg('danger', res.message);
        } else {
            const res = await adminCreateTestimonial(tData, token);
            if (res.success) {
                showMsg('success', 'Changes Saved Successfully!');
                loadAllAdminData(token);
                setModalType(null);
            } else showMsg('danger', res.message);
        }
    };

    const handleApproveTestimonial = async (id) => {
        const res = await adminApproveTestimonial(id, token);
        if (res.success) {
            showMsg('success', 'Review approved and published live!');
            loadAllAdminData(token);
        } else showMsg('danger', res.message);
    };

    const handleDeleteTestimonial = (id) => {
        const test = testimonials.find(t => t.id === id);
        const name = test?.name ? `"${test.name}"` : 'this testimonial';
        setDeleteConfirm({
            title: 'Delete Testimonial',
            message: `Are you sure you want to delete ${name}? This action cannot be undone.`,
            onConfirm: async () => {
                setDeleteConfirm(null);
                const res = await adminDeleteTestimonial(id, token);
                if (res.success) {
                    showMsg('success', 'Testimonial removed.');
                    loadAllAdminData(token);
                } else showMsg('danger', res.message);
            }
        });
    };

    // PAGE BANNER ACTIONS
    const handleSaveBanner = async (e) => {
        e.preventDefault();
        const form = e.target;
        const key = editItem?.key;
        if (!key) return;

        const bannerData = {
            title: form.title.value,
            description: form.description.value,
            bgImage: modalImageUrl || editItem?.bgImage || '/images/banner/6.jpg'
        };

        const res = await adminUpdateBanner(key, bannerData, token);
        if (res.success) {
            showMsg('success', 'Changes Saved Successfully!');
            loadAllAdminData(token);
            setModalType(null);
        } else {
            showMsg('danger', res.message || 'Error updating page banner');
        }
    };

    // INQUIRY ACTIONS
    const handleDeleteInquiry = (id) => {
        setDeleteConfirm({
            title: 'Delete Customer Inquiry',
            message: 'Are you sure you want to delete this customer inquiry? This action cannot be undone.',
            onConfirm: async () => {
                setDeleteConfirm(null);
                const res = await adminDeleteInquiry(id, token);
                if (res.success) {
                    showMsg('success', 'Inquiry removed.');
                    loadAllAdminData(token);
                } else showMsg('danger', res.message);
            }
        });
    };

    // SITE SETTINGS ACTIONS
    const handleSaveSettings = async (e) => {
        e.preventDefault();
        const form = e.target;

        const payload = {
            achievements: {
                yearsExperience: form.yearsExperience.value,
                projectsCompleted: form.projectsCompleted.value,
                clientSatisfaction: form.clientSatisfaction.value
            }
        };

        const res = await adminUpdateSettings(payload, token);
        if (res.success) {
            showMsg('success', 'Changes Saved Successfully!');
            loadAllAdminData(token);
        } else showMsg('danger', res.message || 'Error saving settings');
    };

    const handleSaveCredentials = async (e) => {
        e.preventDefault();
        const form = e.target;
        const username = form.username.value;
        const email = form.email.value;
        const newPassword = form.newPassword.value;

        const res = await adminUpdateCredentials({ username, email, newPassword }, token);
        if (res.success) {
            showMsg('success', 'Admin login credentials updated successfully!');
            if (res.user) {
                localStorage.setItem('adminUser', JSON.stringify(res.user));
            }
            form.newPassword.value = '';
        } else {
            showMsg('danger', res.message || 'Failed to update admin credentials.');
        }
    };

    return (
        <div className="admin-layout">
            {/* SIDEBAR NAVIGATION */}
            <SidebarNav
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                user={user}
                onLogout={handleLogout}
            />

            {/* MAIN CONTENT AREA */}
            <main className="admin-main">
                {/* HEADER BAR */}
                <header className="admin-header">
                    <div className="header-title">
                        <h2>
                            {activeTab === 'dashboard' && 'Dashboard Overview'}
                            {activeTab === 'sliders' && 'Hero Carousel Sliders'}
                            {activeTab === 'banners' && 'Page Banners'}
                            {activeTab === 'products' && 'Products Management'}
                            {activeTab === 'projects' && 'Projects Portfolio'}
                            {activeTab === 'testimonials' && 'Testimonials & Reviews'}
                            {activeTab === 'settings' && 'Site Settings & Metrics'}
                            {activeTab === 'inquiries' && 'Customer Inquiries'}
                        </h2>
                    </div>
                </header>

                {/* FLOATING TOAST NOTIFICATION */}
                {statusMsg.text && (
                    <div className="admin-toast-container">
                        <div className={`admin-toast admin-toast-${statusMsg.type}`}>
                            <i className={`fa ${statusMsg.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`} style={{ fontSize: '18px' }} />
                            <span>{statusMsg.text}</span>
                        </div>
                    </div>
                )}

                {/* TAB VIEWS */}
                {activeTab === 'dashboard' && (
                    <DashboardOverviewTab
                        products={products}
                        sliders={sliders}
                        projects={projects}
                        inquiries={inquiries}
                        testimonials={testimonials}
                        banners={banners}
                        onOpenModal={openModal}
                        getImageUrl={getImageUrl}
                        setActiveTab={setActiveTab}
                    />
                )}

                {activeTab === 'sliders' && (
                    <SlidersTab
                        sliders={sliders}
                        onOpenModal={openModal}
                        onDeleteSlider={handleDeleteSlider}
                        getImageUrl={getImageUrl}
                    />
                )}

                {activeTab === 'banners' && (
                    <PageBannersTab
                        banners={banners}
                        products={products}
                        onOpenModal={openModal}
                        getImageUrl={getImageUrl}
                    />
                )}

                {activeTab === 'products' && (
                    <ProductsTab
                        products={products}
                        onOpenModal={openModal}
                        onDeleteProduct={handleDeleteProduct}
                        getImageUrl={getImageUrl}
                    />
                )}

                {activeTab === 'projects' && (
                    <ProjectsTab
                        projects={projects}
                        onOpenModal={openModal}
                        onDeleteProject={handleDeleteProject}
                        getImageUrl={getImageUrl}
                    />
                )}

                {activeTab === 'testimonials' && (
                    <TestimonialsTab
                        testimonials={testimonials}
                        onOpenModal={openModal}
                        onDeleteTestimonial={handleDeleteTestimonial}
                        onApproveTestimonial={handleApproveTestimonial}
                    />
                )}

                {activeTab === 'settings' && (
                    <SettingsTab
                        settings={settings}
                        achievements={achievements}
                        onSaveSettings={handleSaveSettings}
                    />
                )}

                {activeTab === 'inquiries' && (
                    <InquiriesTab
                        inquiries={inquiries}
                        onDeleteInquiry={handleDeleteInquiry}
                    />
                )}
            </main>

            {/* MODAL POPUPS */}
            {modalType === 'product' && (
                <ProductModal
                    editItem={editItem}
                    modalImageUrl={modalImageUrl}
                    setModalImageUrl={setModalImageUrl}
                    modalBannerImageUrl={modalBannerImageUrl}
                    setModalBannerImageUrl={setModalBannerImageUrl}
                    modalSpecs={modalSpecs}
                    addSpecRow={addSpecRow}
                    updateSpecRow={updateSpecRow}
                    removeSpecRow={removeSpecRow}
                    onSave={handleSaveProduct}
                    onClose={() => setModalType(null)}
                />
            )}

            {modalType === 'slider' && (
                <SliderModal
                    editItem={editItem}
                    modalImageUrl={modalImageUrl}
                    setModalImageUrl={setModalImageUrl}
                    nextOrder={sliders.length + 1}
                    onSave={handleSaveSlider}
                    onClose={() => setModalType(null)}
                />
            )}

            {modalType === 'project' && (
                <ProjectModal
                    editItem={editItem}
                    modalImageUrl={modalImageUrl}
                    setModalImageUrl={setModalImageUrl}
                    modalImageUrls={modalImageUrls}
                    setModalImageUrls={setModalImageUrls}
                    onSave={handleSaveProject}
                    onClose={() => setModalType(null)}
                />
            )}

            {modalType === 'testimonial' && (
                <TestimonialModal
                    editItem={editItem}
                    onSave={handleSaveTestimonial}
                    onClose={() => setModalType(null)}
                />
            )}

            {modalType === 'banner' && (
                <PageBannerModal
                    editItem={editItem}
                    modalImageUrl={modalImageUrl}
                    setModalImageUrl={setModalImageUrl}
                    onSave={handleSaveBanner}
                    onClose={() => setModalType(null)}
                />
            )}
            {deleteConfirm && (
                <ConfirmDeleteModal
                    title={deleteConfirm.title}
                    message={deleteConfirm.message}
                    onConfirm={deleteConfirm.onConfirm}
                    onClose={() => setDeleteConfirm(null)}
                />
            )}
        </div>
    );
};

export default AdminDashboard;
